/**
 * @fileoverview
 * Hero section for the Aideco homepage.
 *
 * This is a client component so that Trans (Lingui i18n) can access
 * the React context from LinguiProvider. The RSC setI18n/getI18n mechanism
 * does not propagate reliably in the Cloudflare Worker runtime; using a
 * client component delegates to I18nProvider React context which works
 * correctly in both SSR and hydration.
 *
 * Content:
 * - Headline: "Transform CAD to Photorealistic Renders"
 * - Subtitle: "AI-Powered Architecture Visualization"
 * - Four stat badges: 3-Day Free Trial, 60s Avg Render, 4K Max Quality, Custom Tailored
 * - Background image with responsive srcset
 *
 * Design:
 * - Dark theme with glass morphism cards matching aideco.us
 * - Responsive layout
 */

"use client";

import { Trans, useLingui } from "@lingui/react/macro";
import Image from "next/image";
import type { CSSProperties } from "react";
import {
  MARKETING_HERO_STATS,
  type MarketingHeroStatId,
} from "@/components/marketing/data/render-credits";
import { featureFlags } from "@/config/features";

function renderHeroStatIcon(iconId: MarketingHeroStatId) {
  switch (iconId) {
    case "free":
      return (
        <svg
          className="h-5 w-5 text-gray-500"
          fill="none"
          height="24"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          width="24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M3 21h18" />
          <path d="M5 21V7l8-4v18" />
          <path d="M19 21V11l-6-4" />
          <rect fill="currentColor" height="2" width="2" x="9" y="9" />
          <rect fill="currentColor" height="2" width="2" x="9" y="13" />
        </svg>
      );
    case "speed":
      return (
        <svg
          className="h-5 w-5 text-gray-500"
          fill="none"
          height="24"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          width="24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
        </svg>
      );
    case "quality":
      return (
        <svg
          className="h-5 w-5 text-gray-500"
          fill="none"
          height="24"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          width="24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect height="14" rx="2" ry="2" width="20" x="2" y="3" />
          <line x1="8" x2="16" y1="21" y2="21" />
          <line x1="12" x2="12" y1="17" y2="21" />
          <text
            dominantBaseline="middle"
            fill="currentColor"
            fontSize="7"
            fontWeight="bold"
            stroke="none"
            textAnchor="middle"
            x="12"
            y="11"
          >
            4K
          </text>
        </svg>
      );
    case "custom":
      return (
        <svg
          className="h-5 w-5 text-gray-500"
          fill="none"
          height="24"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          width="24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="13.5" cy="6.5" r="2.5" />
          <path d="M17 15.5s-3-5-3.5-5-3.5 5-3.5 5" />
          <rect height="20" rx="2" ry="2" width="20" x="2" y="2" />
          <path d="M2 15l5-5 4 4" />
        </svg>
      );
  }
}

/**
 * HeroSection renders the main homepage hero with headline, subtitle,
 * stat badges, and background image.
 *
 * @returns JSX.Element
 */
export function HeroSection() {
  const { i18n, t } = useLingui();

  const heroStatContent: Record<MarketingHeroStatId, { title: string; label: string }> = {
    free: featureFlags.enablePaidGate
      ? { title: t`3-Day`, label: t`Free Trial` }
      : { title: t`Free`, label: t`3/day` },
    speed: { title: t`60s`, label: t`Avg Render` },
    quality: { title: t`4K`, label: t`Max Quality` },
    custom: { title: t`Custom`, label: t`Tailored` },
  };
  const heroAriaLabel = i18n.locale === "vi" ? "Phần mở đầu" : "Hero section";

  return (
    <section
      aria-label={heroAriaLabel}
      className="relative min-h-[900px] w-full overflow-hidden bg-dark-page sm:min-h-[900px] lg:min-h-[1370px] xl:min-h-[1370px]"
      style={{ fontFamily: "var(--font-outfit), system-ui, sans-serif" }}
    >
      {/* Background Image - positioned for proper framing */}
      <div className="absolute inset-0 z-0">
        <Image
          alt="Hero Banner Image"
          className="object-cover object-center lg:origin-top lg:scale-[1.1] lg:-translate-y-[148px]"
          fill
          fetchPriority="high"
          loading="eager"
          quality={85}
          sizes="100vw"
          src="/images/marketing/68629f264c116b32e0b04413_bg.webp"
        />
        {/* Subtle overlay for text readability matching live */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/20" />
      </div>

      {/* Content Overlay - matching live aideco.us structure */}
      <div className="relative z-10 mx-auto flex min-h-[900px] w-full max-w-7xl flex-col px-8 sm:min-h-[900px] sm:px-6 lg:min-h-[1370px] lg:px-8 xl:min-h-[1370px]">
        <div className="flex min-h-full flex-1 flex-col items-center px-5 py-16 text-center sm:py-[60px] lg:pt-[224px]">
          {/* Top Content - Headline and Subtitle */}
          <div className="mb-10 max-w-[800px]">
            {/* Subtitle always above headline (matches live aideco.us) */}
            <p className="mb-4 text-sm font-normal text-white/85">
              <Trans>AI-Powered Architecture Visualization</Trans>
            </p>
            <h1 className="text-[32px] font-semibold leading-[1.2] text-white sm:text-5xl sm:leading-[1.1] md:text-[40px] lg:text-[56px]">
              <Trans>Transform CAD to Photorealistic Renders</Trans>
            </h1>
          </div>

          {/* Bottom Content - Stat Badges - matching live aideco.us glass morphism design */}
          <div className="mt-auto w-full max-w-[900px]">
            <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4 md:gap-5">
              {MARKETING_HERO_STATS.map((badge, index) => (
                <div
                  key={badge.id}
                  className="rounded-[6px] border border-gray-200 bg-white/95 p-4 text-left backdrop-blur-[10px] sm:rounded-[10px] lg:p-[26px] md:[transform:var(--card-y)]"
                  style={{
                    "--card-y": index % 2 === 0 ? "translateY(-20px)" : "translateY(20px)",
                  } as CSSProperties}
                >
                  <h2 className="mb-2 border-b border-gray-200 pb-3 text-2xl font-semibold text-gray-900 max-[479px]:text-[20px] sm:pb-6 sm:text-[32px]">
                    {heroStatContent[badge.id].title}
                  </h2>
                  <div className="flex items-center justify-between gap-2 max-[479px]:flex-col-reverse max-[479px]:items-center max-[479px]:gap-2">
                    <span className="text-sm text-gray-600">{heroStatContent[badge.id].label}</span>
                    <span className="h-5 w-5 text-gray-500 sm:h-6 sm:w-6">
                      {renderHeroStatIcon(badge.id)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Gradient transition to next section (workflow ticker) - seamless blend */}
      <div className="pointer-events-none absolute bottom-0 left-0 right-0 z-20 h-24 bg-gradient-to-t from-[#f5f5f5] via-[#f5f5f5]/80 to-transparent sm:h-28 md:h-32" />
    </section>
  );
}
